import React, { useMemo, useState } from 'react';
import { ChevronDown, Copy, Palette, RotateCcw } from 'lucide-react';
import { ILMSAlert } from '../../../components/ILMSAlert';
import { ILMSButton } from '../../../components/ILMSButton';
import { ILMSInput, ILMSSelect } from '../../../components/ILMSForm';
import { useAuth } from '../../../hooks/useAuth';
import { getApiErrorMessage } from '../../../services/api';
import { cn } from '../../../utils/cn';
import { SmartChart } from '../../../features/charts/SmartChart';
import { CHARTS, PALETTES, type ChartDef } from '../../../features/charts/chartRegistry';
import {
  getAllChartSettings, pruneSettings, saveChartSettings,
  type AllChartSettings, type AxisSettings, type ChartSettings, type SeriesSettings,
} from '../../../features/charts/chartSettings';
import { SaveBar } from './SaveBar';

// ---------- small form pieces ----------
function Group({ title, open: startOpen = false, children }: { title: string; open?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(startOpen);
  return (
    <div className="border-b border-ink-300/15">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-2.5 text-label text-ink-900">
        {title}
        <ChevronDown className={cn('h-4 w-4 text-ink-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open && <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 pb-3">{children}</div>}
    </div>
  );
}

function F({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <label className={cn('flex flex-col gap-1 min-w-0', wide && 'col-span-2')}>
      <span className="text-helper-text text-ink-500">{label}</span>
      {children}
    </label>
  );
}

function Check({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className="flex items-center gap-2 text-secondary text-ink-700 cursor-pointer">
      <input type="checkbox" className="h-4 w-4 accent-brand-green-700" disabled={disabled} checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

/** Empty box = automatic (null). */
function Num({ value, onChange, step = 'any', placeholder = 'Auto', disabled, min, max }: {
  value: number | null | undefined; onChange: (v: number | null) => void; step?: number | 'any'; placeholder?: string; disabled?: boolean; min?: number; max?: number;
}) {
  return (
    <ILMSInput type="number" step={step} min={min} max={max} placeholder={placeholder} disabled={disabled}
      value={value == null ? '' : value} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
  );
}

function Colour({ value, onChange, disabled, allowNone }: { value: string; onChange: (v: string) => void; disabled?: boolean; allowNone?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <input type="color" disabled={disabled} value={value || '#ffffff'} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 shrink-0 cursor-pointer rounded border border-ink-300/40" />
      <span className="text-helper-text text-ink-500 uppercase truncate">{value || 'none'}</span>
      {allowNone && value && <button type="button" className="text-helper-text text-info-600 hover:underline" onClick={() => onChange('')}>none</button>}
    </div>
  );
}

const pick = <T,>(saved: T | undefined, fallback: T): T => (saved === undefined ? fallback : saved);
// Chart-wide "look" an Admin may copy onto every chart in one go.
const LOOK_KEYS: (keyof ChartSettings)[] = ['fontSize', 'labelSize', 'labelHalo', 'legendPosition', 'gridH', 'gridV', 'gridDashed', 'gridColor', 'background', 'border', 'animation', 'barRadius', 'tooltip', 'groupGap', 'barGap'];

export function ChartsPanel() {
  const { can } = useAuth();
  const canEdit = can('System Settings', 'Edit');
  const [drafts, setDrafts] = useState<AllChartSettings>(() => JSON.parse(JSON.stringify(getAllChartSettings())));
  const [chartId, setChartId] = useState(CHARTS[0].id);
  const def: ChartDef = CHARTS.find((c) => c.id === chartId)!;
  const [seriesKey, setSeriesKey] = useState(def.series[0].key);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sample = useMemo(() => def.sample(), [def]);

  const s: ChartSettings = drafts[chartId] ?? {};
  const update = (fn: (cur: ChartSettings) => ChartSettings) => {
    setSaved(false);
    setDrafts((all) => ({ ...all, [chartId]: fn(all[chartId] ?? {}) }));
  };
  const set = (patch: Partial<ChartSettings>) => update((cur) => ({ ...cur, ...patch }));
  const setAxis = (side: 'left' | 'right', patch: Partial<AxisSettings>) => update((cur) => ({ ...cur, [side]: { ...(cur[side] ?? {}), ...patch } }));
  const sd = def.series.find((x) => x.key === seriesKey) ?? def.series[0];
  const ss: SeriesSettings = s.series?.[sd.key] ?? {};
  const setSeries = (patch: Partial<SeriesSettings>) => update((cur) => ({ ...cur, series: { ...(cur.series ?? {}), [sd.key]: { ...(cur.series?.[sd.key] ?? {}), ...patch } } }));

  const choose = (id: string) => {
    setChartId(id);
    setSeriesKey(CHARTS.find((c) => c.id === id)!.series[0].key);
  };
  const applyPalette = (colors: string[]) => update((cur) => {
    const series = { ...(cur.series ?? {}) };
    let n = 0;
    def.series.forEach((x) => {
      series[x.key] = { ...(series[x.key] ?? {}), color: colors[n++ % colors.length], ...(x.alt ? { altColor: colors[n++ % colors.length] } : {}) };
    });
    return { ...cur, series };
  });
  const copyLookToAll = () => {
    const look = Object.fromEntries(LOOK_KEYS.filter((k) => s[k] !== undefined).map((k) => [k, s[k]]));
    setSaved(false);
    setDrafts((all) => Object.fromEntries(CHARTS.map((c) => [c.id, { ...(all[c.id] ?? {}), ...look }])));
  };

  const cleaned = useMemo(() => Object.fromEntries(Object.entries(drafts).map(([k, v]) => [k, pruneSettings(v)]).filter(([, v]) => Object.keys(v as object).length)), [drafts]);
  const dirty = JSON.stringify(cleaned) !== JSON.stringify(getAllChartSettings());
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveChartSettings(cleaned as AllChartSettings);
      setSaved(true);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not save the chart settings.'));
    } finally {
      setSaving(false);
    }
  };

  const dis = !canEdit;
  const leftAxis = { ...def.left, ...(s.left ?? {}) };
  const rightAxis = { ...(def.right ?? {}), ...(s.right ?? {}) };
  const hasBars = def.series.some((x) => (s.series?.[x.key]?.type ?? x.type) === 'bar');
  const stacks = def.series.some((x) => x.stack);
  const sType = ss.type ?? sd.type;

  const axisGroup = (side: 'left' | 'right', a: AxisSettings) => (
    <Group title={side === 'left' ? 'Vertical axis — left' : 'Vertical axis — right'}>
      <Check label="Show axis" disabled={dis} checked={a.show !== false} onChange={(v) => setAxis(side, { show: v })} />
      <Check label="Values in reverse order" disabled={dis} checked={!!a.reverse} onChange={(v) => setAxis(side, { reverse: v })} />
      <F label="Axis title" wide><ILMSInput disabled={dis} value={a.title ?? ''} onChange={(e) => setAxis(side, { title: e.target.value })} /></F>
      <F label="Minimum"><Num disabled={dis} value={a.min} onChange={(v) => setAxis(side, { min: v })} /></F>
      <F label="Maximum"><Num disabled={dis} value={a.max} onChange={(v) => setAxis(side, { max: v })} /></F>
      <F label="Major unit (step)"><Num disabled={dis} value={a.step} min={0} onChange={(v) => setAxis(side, { step: v })} /></F>
      <F label="Label every … steps"><Num disabled={dis} value={a.labelEvery} min={1} step={1} placeholder="Auto" onChange={(v) => setAxis(side, { labelEvery: v ?? undefined })} /></F>
      <F label="Decimal places"><Num disabled={dis} value={a.decimals} min={0} max={4} step={1} onChange={(v) => setAxis(side, { decimals: v })} /></F>
      <F label="Display units">
        <ILMSSelect disabled={dis} value={a.units ?? 'none'} onChange={(e) => setAxis(side, { units: e.target.value as AxisSettings['units'] })}>
          <option value="none">None</option><option value="thousands">Thousands (k)</option><option value="lakhs">Lakhs</option><option value="crores">Crores</option>
        </ILMSSelect>
      </F>
      <F label="Before each number"><ILMSInput disabled={dis} maxLength={6} placeholder="e.g. ₹" value={a.prefix ?? ''} onChange={(e) => setAxis(side, { prefix: e.target.value })} /></F>
      <F label="After each number"><ILMSInput disabled={dis} maxLength={8} placeholder="e.g. % or L" value={a.suffix ?? ''} onChange={(e) => setAxis(side, { suffix: e.target.value })} /></F>
      <Check label="Logarithmic scale" disabled={dis} checked={!!a.log} onChange={(v) => setAxis(side, { log: v })} />
      <Check label="Cut off data outside min / max" disabled={dis} checked={!!a.clip} onChange={(v) => setAxis(side, { clip: v })} />
    </Group>
  );

  return (
    <div className="flex flex-col gap-4">
      {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 min-w-[18rem]">
          <span className="text-label">Chart</span>
          <ILMSSelect value={chartId} onChange={(e) => choose(e.target.value)}>
            {Array.from(new Set(CHARTS.map((c) => c.page))).map((page) => (
              <optgroup key={page} label={page}>
                {CHARTS.filter((c) => c.page === page).map((c) => <option key={c.id} value={c.id}>{c.name}{drafts[c.id] && Object.keys(pruneSettings(drafts[c.id])).length ? '  ✎' : ''}</option>)}
              </optgroup>
            ))}
          </ILMSSelect>
        </label>
        {canEdit && (
          <>
            <ILMSButton variant="outline" icon={<RotateCcw />} onClick={() => { setSaved(false); setDrafts((all) => { const n = { ...all }; delete n[chartId]; return n; }); }}>Reset this chart</ILMSButton>
            <ILMSButton variant="ghost" icon={<Copy />} onClick={copyLookToAll} title="Font sizes, gridlines, legend place, background, border, bar corners and gaps">Copy look to all charts</ILMSButton>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5 items-start">
        <div className="xl:col-span-3 xl:sticky xl:top-0 flex flex-col gap-2">
          <div className="rounded-lg border border-ink-300/20 bg-white p-3">
            <SmartChart key={chartId} def={def} data={sample} settings={s} />
          </div>
          <p className="text-helper-text text-ink-400">Preview with sample figures. Shown on: {def.page}. Click a legend entry to hide / show it.</p>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 text-helper-text text-ink-500"><Palette className="h-3.5 w-3.5" /> Colours:</span>
              {PALETTES.map((p) => (
                <button key={p.id} type="button" title={p.label} onClick={() => applyPalette(p.colors)} className="flex overflow-hidden rounded border border-ink-300/30 hover:ring-2 hover:ring-brand-green-200">
                  {p.colors.slice(0, 5).map((c) => <span key={c} className="h-5 w-3" style={{ background: c }} />)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="xl:col-span-2 rounded-lg border border-ink-300/20 bg-white px-3">
          <Group title="Chart" open>
            <Check label="Show chart title" disabled={dis} checked={pick(s.showTitle, def.showTitle)} onChange={(v) => set({ showTitle: v })} />
            <Check label="Animation" disabled={dis} checked={pick(s.animation, true)} onChange={(v) => set({ animation: v })} />
            <F label="Chart title" wide><ILMSInput disabled={dis} placeholder={def.title} value={s.title ?? ''} onChange={(e) => set({ title: e.target.value })} /></F>
            <F label="Height (px)"><Num disabled={dis} value={pick(s.height, def.height)} min={160} max={900} step={10} onChange={(v) => set({ height: v ?? undefined })} /></F>
            <F label="Text size"><Num disabled={dis} value={pick(s.fontSize, 11)} min={8} max={18} step={1} onChange={(v) => set({ fontSize: v ?? undefined })} /></F>
            <F label="Background"><Colour allowNone disabled={dis} value={s.background ?? ''} onChange={(v) => set({ background: v })} /></F>
            <div className="flex flex-col justify-end gap-2">
              <Check label="Border" disabled={dis} checked={!!s.border} onChange={(v) => set({ border: v })} />
              <Check label="Data table below" disabled={dis} checked={!!s.dataTable} onChange={(v) => set({ dataTable: v })} />
            </div>
          </Group>

          <Group title="Series (each line / bar)" open>
            <F label="Series" wide>
              <ILMSSelect value={sd.key} onChange={(e) => setSeriesKey(e.target.value)}>
                {def.series.map((x) => <option key={x.key} value={x.key}>{s.series?.[x.key]?.name || x.name}</option>)}
              </ILMSSelect>
            </F>
            <F label="Name in legend"><ILMSInput disabled={dis} placeholder={sd.name} value={ss.name ?? ''} onChange={(e) => setSeries({ name: e.target.value })} /></F>
            <F label="Chart type">
              <ILMSSelect disabled={dis} value={sType} onChange={(e) => setSeries({ type: e.target.value as SeriesSettings['type'] })}>
                <option value="bar">Column (bar)</option><option value="line">Line</option><option value="area">Area</option><option value="points">Points only</option>
              </ILMSSelect>
            </F>
            <F label="Colour"><Colour disabled={dis} value={ss.color || sd.color} onChange={(v) => setSeries({ color: v })} /></F>
            {sd.alt && <F label={`Colour — ${sd.alt.label}`}><Colour disabled={dis} value={ss.altColor || sd.alt.color} onChange={(v) => setSeries({ altColor: v })} /></F>}
            <F label="Plot on axis">
              <ILMSSelect disabled={dis} value={ss.axis ?? sd.axis ?? 'left'} onChange={(e) => setSeries({ axis: e.target.value as 'left' | 'right' })}>
                <option value="left">Left (primary)</option><option value="right">Right (secondary)</option>
              </ILMSSelect>
            </F>
            <div className="flex items-end"><Check label="Hide this series" disabled={dis} checked={!!ss.hidden} onChange={(v) => setSeries({ hidden: v })} /></div>
            {sType !== 'bar' && (
              <>
                <F label="Line width"><Num disabled={dis} value={pick(ss.width, sd.width ?? 2)} min={0.5} max={8} step={0.5} onChange={(v) => setSeries({ width: v ?? undefined })} /></F>
                <F label="Line style">
                  <ILMSSelect disabled={dis} value={ss.dash ?? sd.dash ?? 'solid'} onChange={(e) => setSeries({ dash: e.target.value as SeriesSettings['dash'] })}>
                    <option value="solid">Solid</option><option value="dashed">Dashed</option><option value="dotted">Dotted</option><option value="dashdot">Dash-dot</option>
                  </ILMSSelect>
                </F>
                <F label="Line shape">
                  <ILMSSelect disabled={dis} value={ss.curve ?? 'smooth'} onChange={(e) => setSeries({ curve: e.target.value as SeriesSettings['curve'] })}>
                    <option value="smooth">Smoothed</option><option value="straight">Straight</option><option value="step">Steps</option>
                  </ILMSSelect>
                </F>
                <F label="Marker">
                  <ILMSSelect disabled={dis} value={ss.marker ?? 'circle'} onChange={(e) => setSeries({ marker: e.target.value as SeriesSettings['marker'] })}>
                    <option value="circle">Circle</option><option value="square">Square</option><option value="diamond">Diamond</option><option value="triangle">Triangle</option><option value="none">None</option>
                  </ILMSSelect>
                </F>
                <F label="Marker size"><Num disabled={dis} value={pick(ss.markerSize, sType === 'points' ? 4 : 3)} min={1} max={10} step={1} onChange={(v) => setSeries({ markerSize: v ?? undefined })} /></F>
              </>
            )}
            {(sType === 'bar' || sType === 'area') && (
              <>
                <F label="Fill strength (%)"><Num disabled={dis} value={pick(ss.opacity, sType === 'area' ? 35 : 100)} min={10} max={100} step={5} onChange={(v) => setSeries({ opacity: v ?? undefined })} /></F>
                <div className="flex items-end"><Check label="Gradient fill" disabled={dis} checked={!!ss.gradient} onChange={(v) => setSeries({ gradient: v })} /></div>
              </>
            )}
            <F label="Data labels">
              <ILMSSelect disabled={dis} value={ss.labels ?? 'inherit'} onChange={(e) => setSeries({ labels: e.target.value as SeriesSettings['labels'] })}>
                <option value="inherit">As the chart ({sd.labels === false ? 'off' : 'on'})</option><option value="on">On</option><option value="off">Off</option>
              </ILMSSelect>
            </F>
            <F label="Label position">
              <ILMSSelect disabled={dis} value={ss.labelPosition ?? sd.labelPosition ?? 'auto'} onChange={(e) => setSeries({ labelPosition: e.target.value as SeriesSettings['labelPosition'] })}>
                <option value="auto">Automatic</option><option value="top">Above</option><option value="bottom">Below</option><option value="center">Centre</option>
                {sType === 'bar' && <option value="insideTop">Inside top</option>}
              </ILMSSelect>
            </F>
            <F label="Label colour"><Colour allowNone disabled={dis} value={ss.labelColor ?? ''} onChange={(v) => setSeries({ labelColor: v })} /></F>
            <F label="Trendline">
              <ILMSSelect disabled={dis} value={ss.trend ?? 'none'} onChange={(e) => setSeries({ trend: e.target.value as SeriesSettings['trend'] })}>
                <option value="none">None</option><option value="linear">Linear</option><option value="moving">Moving average</option>
              </ILMSSelect>
            </F>
            {ss.trend === 'moving' && <F label="Average of … points"><Num disabled={dis} value={pick(ss.trendPeriod, 3)} min={2} max={30} step={1} onChange={(v) => setSeries({ trendPeriod: v ?? undefined })} /></F>}
          </Group>

          {(hasBars || stacks) && (
            <Group title="Columns (bars)">
              {stacks && (
                <F label="Grouping" wide>
                  <ILMSSelect disabled={dis} value={s.stacking ?? 'stacked'} onChange={(e) => set({ stacking: e.target.value as ChartSettings['stacking'] })}>
                    <option value="stacked">Stacked</option><option value="clustered">Clustered (side by side)</option><option value="percent">100% stacked</option>
                  </ILMSSelect>
                </F>
              )}
              <F label="Gap between groups (%)"><Num disabled={dis} value={pick(s.groupGap, 10)} min={0} max={90} step={5} onChange={(v) => set({ groupGap: v ?? undefined })} /></F>
              <F label="Gap between bars (px, minus = overlap)"><Num disabled={dis} value={pick(s.barGap, 4)} min={-30} max={30} step={1} onChange={(v) => set({ barGap: v ?? undefined })} /></F>
              <F label="Rounded corners (px)"><Num disabled={dis} value={pick(s.barRadius, def.barRadius)} min={0} max={20} step={1} onChange={(v) => set({ barRadius: v ?? undefined })} /></F>
              <F label="Widest bar (px)"><Num disabled={dis} value={s.maxBarSize ?? def.maxBarSize ?? null} min={4} max={200} step={2} onChange={(v) => set({ maxBarSize: v ?? undefined })} /></F>
              {stacks && <div className="col-span-2"><Check label="Show the stack total above each column" disabled={dis} checked={pick(s.showTotal, def.showTotal ?? false)} onChange={(v) => set({ showTotal: v })} /></div>}
            </Group>
          )}

          <Group title="Data labels">
            <F label="Which points">
              <ILMSSelect disabled={dis} value={s.labelMode ?? def.labelMode} onChange={(e) => set({ labelMode: e.target.value as ChartSettings['labelMode'] })}>
                <option value="auto">Automatic (all on short ranges, else latest)</option><option value="all">Every point</option><option value="last">Latest only</option><option value="none">None</option>
              </ILMSSelect>
            </F>
            <F label="Label size"><Num disabled={dis} value={pick(s.labelSize, Math.max(9, pick(s.fontSize, 11) - 1))} min={7} max={18} step={1} onChange={(v) => set({ labelSize: v ?? undefined })} /></F>
            <F label="Decimal places"><Num disabled={dis} value={s.labelDecimals} min={0} max={4} step={1} onChange={(v) => set({ labelDecimals: v })} /></F>
            <div className="flex items-end"><Check label="White outline (easier to read)" disabled={dis} checked={pick(s.labelHalo, true)} onChange={(v) => set({ labelHalo: v })} /></div>
          </Group>

          <Group title="Horizontal axis">
            <Check label="Show axis" disabled={dis} checked={s.xShow !== false} onChange={(v) => set({ xShow: v })} />
            <Check label="Categories in reverse order" disabled={dis} checked={!!s.xReverse} onChange={(v) => set({ xReverse: v })} />
            <F label="Axis title"><ILMSInput disabled={dis} value={s.xTitle ?? ''} onChange={(e) => set({ xTitle: e.target.value })} /></F>
            <F label="Label angle">
              <ILMSSelect disabled={dis} value={pick(s.xAngle, def.xAngle ?? 0)} onChange={(e) => set({ xAngle: Number(e.target.value) })}>
                {[0, -20, -30, -45, -60, -90].map((a) => <option key={a} value={a}>{a === 0 ? 'Straight' : `${Math.abs(a)}°`}</option>)}
              </ILMSSelect>
            </F>
            <div className="col-span-2"><Check label="Show every label (no skipping)" disabled={dis} checked={pick(s.xAllLabels, def.xAllLabels ?? false)} onChange={(v) => set({ xAllLabels: v })} /></div>
          </Group>

          {axisGroup('left', leftAxis)}
          {def.series.some((x) => (s.series?.[x.key]?.axis ?? x.axis) === 'right') && axisGroup('right', rightAxis)}

          <Group title="Gridlines">
            <Check label="Horizontal lines" disabled={dis} checked={pick(s.gridH, true)} onChange={(v) => set({ gridH: v })} />
            <Check label="Vertical lines" disabled={dis} checked={pick(s.gridV, def.gridV)} onChange={(v) => set({ gridV: v })} />
            <Check label="Dashed" disabled={dis} checked={pick(s.gridDashed, def.gridDashed)} onChange={(v) => set({ gridDashed: v })} />
            <F label="Line colour"><Colour disabled={dis} value={s.gridColor ?? ''} allowNone onChange={(v) => set({ gridColor: v })} /></F>
            <F label="Stronger line every (left axis)" wide><Num disabled={dis} value={s.majorEvery !== undefined ? s.majorEvery : def.majorEvery ?? null} min={0} placeholder="None" onChange={(v) => set({ majorEvery: v })} /></F>
          </Group>

          <Group title="Legend and tooltip">
            <Check label="Show legend" disabled={dis} checked={pick(s.legend, def.legend)} onChange={(v) => set({ legend: v })} />
            <Check label="Tooltip on hover" disabled={dis} checked={s.tooltip !== false} onChange={(v) => set({ tooltip: v })} />
            <F label="Legend position" wide>
              <ILMSSelect disabled={dis} value={s.legendPosition ?? 'bottom'} onChange={(e) => set({ legendPosition: e.target.value as ChartSettings['legendPosition'] })}>
                <option value="bottom">Bottom</option><option value="top">Top</option><option value="right">Right</option><option value="left">Left</option>
              </ILMSSelect>
            </F>
          </Group>

          <Group title="Target / reference lines">
            <F label='Lines on the left axis, e.g. "4.5 Target, 3.5 Minimum"' wide>
              <ILMSInput disabled={dis} value={s.refLines ?? ''} onChange={(e) => set({ refLines: e.target.value })} />
            </F>
            <F label="Line colour"><Colour disabled={dis} value={s.refColor || '#d6402f'} onChange={(v) => set({ refColor: v })} /></F>
          </Group>
          <div className="h-2" />
        </div>
      </div>

      <SaveBar canEdit={canEdit} dirty={dirty} saving={saving} saved={saved} onSave={save} saveLabel="Save all charts"
        onReset={() => { if (window.confirm('Put every chart back to how it was built?')) { setSaved(false); setDrafts({}); } }} resetLabel="Reset all charts" />
    </div>
  );
}
